import React from 'react';
import {
  IconButton,
  ICON_BUTTON_SIZE_TB,
  TOOLBAR_DENSITY_COMPACT,
  TOOLBAR_VARIANT_FLOATING,
  TOOLTIP_PLACEMENT_TOP,
  Toolbar,
} from '@aibuild/ui';
import { useEditorContext } from '../useEditorContext';
import { ToolbarWrapper } from './CanvasToolbar.styled';
import MaterialIcon from './MaterialIcon';

export interface CanvasToolbarBottomProps {
  dataTestId?: string;
}

/**
 * Bottom toolbar tooltips float ABOVE the trigger so the tip lifts into
 * the canvas rather than under the bottom edge of the viewport.
 */
const TOOLTIP_PLACEMENT = TOOLTIP_PLACEMENT_TOP;

/**
 * Inline SVG glyph for the bezier wire-style toggle. Hand-rolled (rather
 * than a Material Symbol) because the design needs a stroked curve that
 * adopts `currentColor` -- material symbols are filled glyphs whose
 * weight metrics fight the toolbar's hover affordance.
 *
 * Geometry copied verbatim from the handoff prototype
 * (CanvasToolbar.jsx:431-434) so the two shapes read identically across
 * the prototype <-> production migration.
 */
const BezierIcon: React.FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 17 C 8 17, 8 7, 13 7 C 18 7, 18 17, 21 17" />
  </svg>
);

/** Inline SVG glyph for the orthogonal/fillet wire-style toggle. */
const OrthoIcon: React.FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 17 L 6 17 Q 9 17 9 14 L 9 10 Q 9 7 12 7 L 15 7 Q 18 7 18 10 L 18 14 Q 18 17 21 17" />
  </svg>
);

/**
 * Floating bottom canvas toolbar -- mirrors the handoff prototype's
 * bottom `.ctb--bottom` row (CanvasToolbar.jsx:414-488).
 *
 * Identical in DRAFT and PUBLISHED state. Groups, in order:
 *
 *   [Zoom out, Zoom in, Fit, Center] |
 *   [Bezier, Ortho]                  |
 *   [Auto-arrange]                   |
 *   [Collapse all, Expand all]       |
 *   [Grid, Minimap]
 */
const CanvasToolbarBottom: React.FC<CanvasToolbarBottomProps> = ({
  dataTestId = 'canvas-toolbar-bottom',
}) => {
  const ctx = useEditorContext();
  const isBezier = ctx.wireStyle === 'bezier';
  const isOrtho = ctx.wireStyle === 'ortho';

  return (
    <ToolbarWrapper $position="bottom" data-testid={`${dataTestId}__wrapper`}>
      <Toolbar
        variant={TOOLBAR_VARIANT_FLOATING}
        density={TOOLBAR_DENSITY_COMPACT}
        dividerInset={8}
        ariaLabel="Canvas overlays"
        dataTestId={dataTestId}
      >
        <Toolbar.Group ariaLabel="View">
          <IconButton
            ariaLabel="Zoom out"
            tooltip="Zoom out"
            tooltipPlacement={TOOLTIP_PLACEMENT}
            size={ICON_BUTTON_SIZE_TB}
            icon={<MaterialIcon name="zoom_out" />}
            onClick={ctx.zoomOut}
            dataTestId={`${dataTestId}__zoom-out`}
          />
          <IconButton
            ariaLabel="Zoom in"
            tooltip="Zoom in"
            tooltipPlacement={TOOLTIP_PLACEMENT}
            size={ICON_BUTTON_SIZE_TB}
            icon={<MaterialIcon name="zoom_in" />}
            onClick={ctx.zoomIn}
            dataTestId={`${dataTestId}__zoom-in`}
          />
          <IconButton
            ariaLabel="Fit to view"
            tooltip="Fit graph to view"
            tooltipPlacement={TOOLTIP_PLACEMENT}
            size={ICON_BUTTON_SIZE_TB}
            icon={<MaterialIcon name="fit_screen" />}
            onClick={ctx.fitView}
            dataTestId={`${dataTestId}__fit`}
          />
          <IconButton
            ariaLabel="Center"
            tooltip="Center on graph"
            tooltipPlacement={TOOLTIP_PLACEMENT}
            size={ICON_BUTTON_SIZE_TB}
            icon={<MaterialIcon name="filter_center_focus" />}
            onClick={ctx.recenter}
            dataTestId={`${dataTestId}__center`}
          />
        </Toolbar.Group>
        <Toolbar.Group ariaLabel="Wire style">
          <IconButton
            ariaLabel="Bezier wires"
            tooltip="Bezier wires"
            tooltipPlacement={TOOLTIP_PLACEMENT}
            size={ICON_BUTTON_SIZE_TB}
            icon={<BezierIcon />}
            isOpen={isBezier}
            onClick={() => ctx.setWireStyle('bezier')}
            dataTestId={`${dataTestId}__wire-bezier`}
          />
          <IconButton
            ariaLabel="Fillet wires"
            tooltip="Fillet wires"
            tooltipPlacement={TOOLTIP_PLACEMENT}
            size={ICON_BUTTON_SIZE_TB}
            icon={<OrthoIcon />}
            isOpen={isOrtho}
            onClick={() => ctx.setWireStyle('ortho')}
            dataTestId={`${dataTestId}__wire-ortho`}
          />
        </Toolbar.Group>
        <Toolbar.Group ariaLabel="Layout">
          <IconButton
            ariaLabel="Auto-arrange nodes"
            tooltip="Auto-arrange nodes"
            tooltipPlacement={TOOLTIP_PLACEMENT}
            size={ICON_BUTTON_SIZE_TB}
            icon={<MaterialIcon name="account_tree" />}
            onClick={ctx.autoArrange}
            dataTestId={`${dataTestId}__auto-arrange`}
          />
        </Toolbar.Group>
        <Toolbar.Group ariaLabel="Nodes">
          <IconButton
            ariaLabel="Collapse all nodes"
            tooltip="Collapse all nodes"
            tooltipPlacement={TOOLTIP_PLACEMENT}
            size={ICON_BUTTON_SIZE_TB}
            icon={<MaterialIcon name="unfold_less" />}
            onClick={() => ctx.setAllNodesCollapsed(true)}
            dataTestId={`${dataTestId}__collapse-all`}
          />
          <IconButton
            ariaLabel="Expand all nodes"
            tooltip="Expand all nodes"
            tooltipPlacement={TOOLTIP_PLACEMENT}
            size={ICON_BUTTON_SIZE_TB}
            icon={<MaterialIcon name="unfold_more" />}
            onClick={() => ctx.setAllNodesCollapsed(false)}
            dataTestId={`${dataTestId}__expand-all`}
          />
        </Toolbar.Group>
        <Toolbar.Group ariaLabel="Display">
          <IconButton
            ariaLabel={ctx.gridOn ? 'Hide grid' : 'Show grid'}
            tooltip={ctx.gridOn ? 'Hide grid' : 'Show grid'}
            tooltipPlacement={TOOLTIP_PLACEMENT}
            size={ICON_BUTTON_SIZE_TB}
            icon={<MaterialIcon name="grid_on" />}
            isOpen={ctx.gridOn}
            onClick={() => ctx.setGridOn(!ctx.gridOn)}
            dataTestId={`${dataTestId}__grid`}
          />
          <IconButton
            ariaLabel={ctx.miniOn ? 'Hide minimap' : 'Show minimap'}
            tooltip={ctx.miniOn ? 'Hide minimap' : 'Show minimap'}
            tooltipPlacement={TOOLTIP_PLACEMENT}
            size={ICON_BUTTON_SIZE_TB}
            icon={<MaterialIcon name="map" />}
            isOpen={ctx.miniOn}
            onClick={() => ctx.setMiniOn(!ctx.miniOn)}
            dataTestId={`${dataTestId}__minimap`}
          />
        </Toolbar.Group>
      </Toolbar>
    </ToolbarWrapper>
  );
};

export default CanvasToolbarBottom;
